// /src/components/shared/TagInput.jsx
// Reusable tag input component with optional count cap.
// Splits input on commas, normalises case, deduplicates.
// caseMode: 'upper' (default, for MyTags) or 'lower' (for directory tags).
// singleWord: when true, splits on whitespace too and applies conservative de-pluralisation.

import React, { useState } from 'react';
import { TAGS_MAX } from '../../utils/validationConstants';

/**
 * Conservative de-pluralisation: strip trailing 's' only when the word
 * doesn't end in ss/us/is/os and is longer than 3 chars.
 * Avoids mangling words like glass, business, dress, bus, thesis.
 */
function dePluralise(word) {
  if (word.length <= 3) return word;
  if (!word.endsWith('s')) return word;
  if (/(?:ss|us|is|os)$/i.test(word)) return word;
  return word.slice(0, -1);
}

/**
 * @param {Object} props
 * @param {string[]} props.tags - Current tags array
 * @param {function} props.onAdd - Called with array of new tags to add
 * @param {function} props.onRemove - Called with tag string to remove
 * @param {number} [props.max] - Maximum tag count (defaults to TAGS_MAX)
 * @param {string} [props.placeholder] - Input placeholder text
 * @param {string} [props.addLabel] - Label for the Add button
 * @param {string} [props.counterText] - e.g. "3 of 5 tags" — pass pre-formatted from parent
 * @param {'upper'|'lower'} [props.caseMode] - 'upper' (default) or 'lower'
 * @param {boolean} [props.singleWord] - When true, split on whitespace and de-pluralise
 */
function TagInput({ tags = [], onAdd, onRemove, max = TAGS_MAX, placeholder = 'Add a tag', addLabel = 'Add', counterText, caseMode = 'upper', singleWord = false }) {
  const [input, setInput] = useState('');

  const handleAdd = () => {
    if (!input.trim()) return;

    // Split on commas first, then optionally on whitespace
    let parts = input.split(',');
    if (singleWord) {
      parts = parts.flatMap(p => p.trim().split(/\s+/));
    }

    const applyCase = caseMode === 'lower' ? s => s.toLowerCase() : s => s.toUpperCase();

    const newTags = parts
      .map(tag => applyCase(tag.trim()))
      .map(tag => singleWord ? dePluralise(tag) : tag)
      .filter(tag => tag && !tags.includes(tag));

    if (newTags.length > 0) {
      // Enforce max: only add up to remaining capacity
      const remaining = max - tags.length;
      const tagsToAdd = newTags.slice(0, remaining);
      if (tagsToAdd.length > 0) {
        onAdd(tagsToAdd);
      }
    }
    setInput('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const atMax = tags.length >= max;

  return (
    <div>
      {/* Input row */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={atMax ? '' : placeholder}
          disabled={atMax}
          style={{
            flex: 1,
            padding: '12px 16px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none',
            opacity: atMax ? 0.5 : 1
          }}
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={atMax || !input.trim()}
          style={{
            padding: '12px 24px',
            backgroundColor: atMax ? '#9ca3af' : '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: atMax ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          {addLabel}
        </button>
      </div>

      {/* Counter */}
      {counterText && (
        <div style={{ fontSize: '13px', color: atMax ? '#dc2626' : '#6b7280', marginBottom: '8px' }}>
          {counterText}
        </div>
      )}

      {/* Tag pills */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        minHeight: '44px',
        padding: '12px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        {tags.map((tag, index) => (
          <button
            key={index}
            type="button"
            onClick={() => onRemove(tag)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              backgroundColor: '#14b8a6',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            <span>{tag}</span>
            <span style={{ fontSize: '16px', lineHeight: 1 }}>&times;</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default TagInput;
